import React, { useMemo } from 'react';

import { Chip, IconButton, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import Table from 'components/Table';
import type { DsarCaseAsset } from 'config/api/dsar/dsar.types';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import search_messages from 'messages/search_messages';
import PATHS from 'router/PATHS';
import type { TableDataMappingRow } from 'types/Table';

import useStyles from '../DsarCaseAssetsPage.styles';

type Props = {
  data: DsarCaseAsset[];
  filtersService?: any;
  loading: boolean;
  sort: { key: string | null; direction: string | null };
  onSort: (key: string | null, direction: string | null) => void;
  onSearch: (searchTerm: string) => void;
  onOpen: (caseAsset: DsarCaseAsset) => void;
  onEdit: (caseAsset: DsarCaseAsset) => void;
  searchValue: string;
};

const formatDate = (value?: string | null) => (value ? value.split('T')[0] : '-');

const buildDetailPath = (id: number) => PATHS.DSAR_CASE_ASSETS_DETAIL.replace(':id', String(id));

const DsarCaseAssetsTable: React.FC<Props> = ({ data, filtersService, loading, sort, onSort, onSearch, onOpen, onEdit, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const getRowHref = React.useCallback((caseAsset: DsarCaseAsset) => buildDetailPath(caseAsset.id), []);

  const dataMapping: TableDataMappingRow<DsarCaseAsset>[] = useMemo(
    () => [
      {
        id: 'caseReference',
        width: '12%',
        label: t(dsar_messages.case_assets.table.case_reference),
        get: caseAsset => (
          <Link className={styles.tableLink} to={buildDetailPath(caseAsset.id)}>
            {caseAsset.dsarCase.caseReference}
          </Link>
        ),
      },
      {
        id: 'assetName',
        width: '13%',
        label: t(dsar_messages.case_assets.table.asset_name),
        get: caseAsset => (
          <Link className={styles.tableLink} to={buildDetailPath(caseAsset.id)}>
            {caseAsset.asset.name}
          </Link>
        ),
      },
      {
        id: 'owner',
        width: '15%',
        label: t(dsar_messages.case_assets.table.owner),
        get: caseAsset => (
          <div className={styles.stackedCell}>
            <span>{caseAsset.asset.owner.fullName}</span>
            <Typography className={styles.mutedText} variant='caption'>
              {caseAsset.asset.owner.email}
            </Typography>
          </div>
        ),
      },
      {
        id: 'requestTypes',
        width: '16%',
        blockSorting: true,
        label: t(dsar_messages.case_assets.table.request_types),
        get: caseAsset => (
          <div className={styles.requestTypes}>
            {caseAsset.requestTypeResults.length ? (
              caseAsset.requestTypeResults.map(result => (
                <Chip key={result.id} className={styles.requestTypeChip} label={result.requestType.name} size='small' />
              ))
            ) : (
              <span>{t(general_messages.no_data)}</span>
            )}
          </div>
        ),
      },
      {
        id: 'dueDate',
        width: '10%',
        label: t(dsar_messages.case_assets.table.due_date),
        get: caseAsset => <span className={styles.noWrap}>{formatDate(caseAsset.dueDate)}</span>,
      },
      {
        id: 'status',
        width: '10%',
        label: t(dsar_messages.case_assets.table.status),
        get: caseAsset => <span>{caseAsset.status.name}</span>,
      },
      {
        id: 'dataFound',
        width: '10%',
        label: t(dsar_messages.case_assets.table.data_found),
        get: caseAsset => <span>{caseAsset.dataFound.name}</span>,
      },
      {
        id: 'completedAt',
        width: '8%',
        label: t(dsar_messages.case_assets.table.completed_at),
        get: caseAsset => <span className={styles.noWrap}>{formatDate(caseAsset.completedAt)}</span>,
      },
      {
        id: 'actions',
        width: '6%',
        blockSorting: true,
        isAddon: true,
        label: t(dsar_messages.case_assets.table.actions),
        get: caseAsset => {
          const isCompleted = caseAsset.status.code === 'completed' || Boolean(caseAsset.completedAt);
          const editLabel = isCompleted ? t(dsar_messages.case_assets.actions.reopen) : t(dsar_messages.case_assets.actions.edit);

          return (
            <div className={styles.actionsCell}>
              <ContextMenu
                ariaLabel={t(dsar_messages.case_assets.table.actions)}
                elements={[
                  { label: t(dsar_messages.case_assets.actions.open), onClick: () => onOpen(caseAsset) },
                  { label: editLabel, onClick: () => onEdit(caseAsset) },
                ]}
                id={`dsar-case-asset-${caseAsset.id}`}
              >
                <IconButton size='small'>
                  <MoreIcon />
                </IconButton>
              </ContextMenu>
            </div>
          );
        },
      },
    ],
    [onEdit, onOpen, styles, t],
  );

  return (
    <>
      <div className={styles.searchBarOuterWrapper}>
        <div className={styles.searchInputCell}>
          <InputWithDebouncedAction
            action={onSearch}
            formControl={{ size: 'small' }}
            label={t(search_messages.input_label)}
            value={searchValue}
          />
        </div>
      </div>
      <div
        aria-busy={loading}
        aria-label={t(dsar_messages.case_assets.table.aria_label)}
        className={styles.tableScrollWrapper}
        role='region'
      >
        <Table
          clickableRow
          data={data}
          dataMapping={dataMapping}
          filtersService={filtersService}
          getRowHref={getRowHref}
          onSort={onSort}
          refetching={loading}
          sort={sort}
        />
      </div>
    </>
  );
};

export default DsarCaseAssetsTable;
